// import videoList from '../data/videoList';
import VIDEOS_DATA from '../data/videos_data';
import * as Pioneer from 'pioneer';
import {
	VITAL_CATEGORIES
} from '../config/constants';
import ar from '../languages/ar';

import { lightStore, videoStore, refsStore, eventsStore, poiStore, gmapStore } from './globalState';

/**
 * A manager to control the loading and playback of videos.
 *
 * This manager should NOT:
 *  - control the state of the videos, eg. playing / paused. That should be done in the react component.
 *  - store any element references.
 *
 * There is currently one exception to the above rules. In the case of a global video, we need to use and
 * pass a video HTML element directly to the threejs texture. Controlling this in here rather than react is
 * currently the best solution although it probably should be revisited in the future.

 * This manager SHOULD:
 * - be a bridge between the React component, and the pioneer/scene/router methods that need to be called on load / unload.
 */
class VideoManager {
	/**
	 * Constructor
	 * @param {object} app A reference to app.js
	 */
	constructor(app) {
		/**
		 * We should not passing the whole app component state here.
		 * This is a temp solution and requires improved argument passing to the methods.
		 */
		this._app = app;

		const { get } = refsStore.stateSnapshot;
		this._pioneer = this._app._pioneer;
		this._scene = this._app._scene;
		this._router = get('route');
		this._videoTexture = null;

		this.queryName = 'videoId';
		this.videoList = Array.from(VIDEOS_DATA);

		this.toggleVideoPlayPause = this.toggleVideoPlayPause.bind(this);
		this.getVideoType = this.getVideoType.bind(this);
		this.getVideo = this.getVideo.bind(this);
		this.getVideoDate = this.getVideoDate.bind(this);
		this.getVideoDropdownEntries = this.getVideoDropdownEntries.bind(this);
		this.getVideoIdFromTitle = this.getVideoIdFromTitle.bind(this);
	}

	/**
	 * Get list of related videos
	 * @param {string} query
	 * @returns {Array}
	 */
	getRelatedVideos(query) {
		const relatedList = [];
		this.videoList.forEach(video => {
			video.datasetGroups[0].related.every(relatedDataset => {
				if (relatedDataset === query) {
					relatedList.push(video.datasetGroups[0]);
					return false;
				}
				return true;
			});
		});
		return relatedList;
	}

	/**
	 * Returns an array of objects to add to a dropdown box in mobile details overlay and dataset tools dropdown
	 * @param {string} vitalValue
	 * @returns {Array}
	 */
	getVideoDropdownEntries(vitalValue) {
		const videoDropdownEntryArray = [];
		const relatedVideos = this.getRelatedVideos(vitalValue);
		relatedVideos.forEach(entry => {
			const category = entry.dropDownTitle ? ' (' + entry.dropDownTitle + ')' : null;
			const dropdownTitle = entry.title + category;
			const dropdownId = 0 - this.getVideoIdFromTitle(entry.title);
			const dropdownEntry = {
				id: dropdownId,
				menuTitle: dropdownTitle,
				category: entry.type
			};
			videoDropdownEntryArray.push(dropdownEntry);
		});
		return videoDropdownEntryArray;
	}

	/**
	 * Returns video type for given ID.
	 * @param {number} videoId
	 * @returns
	 */
	getVideoType(videoId) {
		const video = this.getVideo(videoId);
		if (video === null) {
			return null;
		}
		return video.type;
	}

	/**
 	* Get the video from the ID.
 	* @param {*} videoId
 	* @returns
 	*/
	getVideo(videoId) {
		if (videoId === null) {
			return null;
		}
		const video = this.videoList[videoId - 1]?.datasetGroups[0];
		if (video === null || video === undefined) {
			return null;
		}
		return video;
	}

	/**
	 * Get the thumbnail source for the given videoId
	 * @param {string} videoId
	 * @returns
	 */

	getVideoThumbSrc(videoId) {
		return this.getVideo(videoId)?.sphereMapThumbSrc || null;
	}

	/**
	 * Get the title for the given videoId
	 * @param {string} videoId
	 * @returns {string}
	 */
	getVideoThumbTitle(videoId) {
		return this.getVideo(videoId)?.title || null;
	}

	/**
	 * Gets the left panel content for a videoId and returns an object with the content.
	 * @param {number} videoId
	 * @returns {object}
	 */
	getVideoLeftPanelContent(videoId) {
		const video = this.getVideo(videoId);
		if (video === null) {
			return null;
		}
		const data = {
			category: VITAL_CATEGORIES[1][1],
			title: video.title,
			subtitle: video.dateRange,
			descriptions: [{ title: ar.about_this_video, text: video.description, subtitle: video.dateRange }]
		};
		return data;
	}

	/**
	 * Returns Date String for given ID or empty string.
	 * @param {number} videoId
	 * @returns {string}
	 */
	getVideoDate(videoId) {
		const video = this.getVideo(videoId);
		if (!video) {
			return null;
		}
		return video.dateRange || null;
	}

	/**
	 * Sets id of current playing video
	 * @param {string} videoId
	 */
	setCurrentVideoId(videoId) {
		videoStore.setGlobalState({ videoId: videoId });
	}

	/**
	 * Get video entry from title
	 * @param {string} title
	 * @returns index of the video in the file.
	 */
	getVideoIdFromTitle(title) {
		for (let i = 1; i <= this.videoList.length; i++) {
			const video = this.getVideo(i);
			if (title.includes(video.title)) {
				return i;
			}
		}
		return null;
	}

	/**
	 * Get the overlay toggle function for the video back button on mobile
	 * @returns {function}
	 */
	getVideoBackButtonOverlayFunction() {
		return this._videoBackButtonOverlayFunction;
	}

	/**
	 * Set the overlay toggle function for the video back button on mobile
	 * @param {function} overlayFunction
	 */
	setVideoBackButtonOverlayFunction(overlayFunction) {
		this._videoBackButtonOverlayFunction = overlayFunction;
	}

	/**
	 * Sets the video query in the url.
	 * If query is null, remove it from the url.
	 * @param {string} query
	 */
	setVideoQuery(query) {
		const queryObj = query === null ?
			{ __remove: [this.queryName] } :
			{ [this.queryName]: query };
		const updatedQuery = this._router.updateQuery(queryObj);
		this._router.navigate(updatedQuery);
	}

	/**
	 * Get the video data from the videoList and Call the appropriate load function.
	 * @param {string} videoId
	 */
	async loadVideo(videoId) {
		const video = this.getVideo(videoId);
		// Warn if there's no video data.
		if (!video) {
			console.warn(`No video data for video with id, ${videoId}`);
			return;
		}
		// Check if we need to load a global video.
		if (video.type === 'global') {
			await this.loadGlobalVideo(video.src);
		} else if (video.type === 'geoLocated') {
			await this.loadGeolocatedVideo(videoId);
		} else if (video.type === 'modal' || video.type === 'static') {
			const { get } = refsStore.stateSnapshot;
			const offset = 18;
			get('camera').offsetPioneerViewportLeft(offset);
		}
		videoStore.setGlobalState({ videoId: videoId, isVideoPlaying: true, isVideoLoading: false });
		// Disable event markers
    //merge questions this is removed on theirs
		eventsStore.setGlobalState({ showOnGlobe: false, showLatest: false });
		poiStore.setGlobalState({ showOnGlobePoI: false, showLatestPoI: false });
		gmapStore.setGlobalState({ showOnGlobeGmap: false, showLatestGmap: false });
	}


	/**
	 * Loads a video as texture on the earth.
	 *
	 * Future work: ***
	 * 1) Be able to pass autoplay and loop into the setTexture
	 * @param {@string} url
	 */
	async loadGlobalVideo(url) {
		const { get } = refsStore.stateSnapshot;
		const earth = this._scene.getEntity('earth');
		const earthSpheroidComponent = earth.getComponentByType('spheroidLOD');
		const sceneManager = get('scene');
		sceneManager.addLoading('earth', 'video');

		// Call getLoadedPromise first to make sure avoid a race condition when multiple textures are loading to an entitiy.
		earthSpheroidComponent.setForceLoaded(true);
		await earthSpheroidComponent.getLoadedPromise();
		videoStore.setGlobalState({ isVideoPlaying: false, isVideoLoading: true });

		// Hide entity to show video
		const vitalSign = this._app.state.vitalsign;
		if (vitalSign === 'visible_earth') {
			get('visibleEarth')?.hide(true);
		}

		// Set light global state to true.
		lightStore.setGlobalState({ fill: true });

		this._oldTextureMapping = earthSpheroidComponent.getMapping();
		earthSpheroidComponent.setMapping('cylinder');
		earthSpheroidComponent.setTexture('color', url);
		earthSpheroidComponent.setEnabled(true);
		earth.get('atmosphere').setEnabled(false);
		sceneManager.hideAllSC();
		sceneManager.displayEventPlacemarks(false);

		// Hide features on globe for video
		if (vitalSign === 'satellites' || vitalSign === 'visible_earth') {
			earthSpheroidComponent.setFeature('normalMap', false);
			earthSpheroidComponent.setFeature('nightMap', false);
			earthSpheroidComponent.setFeature('decalMap', false);
			earthSpheroidComponent.setFeature('specularMap', false);
		}
		await this._app.pioneer.waitUntilNextFrame();
		await earthSpheroidComponent.getLoadedPromise();
		this._videoTexture = earthSpheroidComponent.getThreeJsMaterials()[0].uniforms.colorTexture.value.image;
		this._videoTexture.loop = true;
		this._app.get('scene').removeLoading('earth', 'video');
		this._videoTexture.play();
	}

	/**
	 * Loads a video as texture on a data patch.
	 * @param {@number} videoId
	 */
	async loadGeolocatedVideo(videoId) {
		const video = this.getVideo(videoId);
		if (video === null || video === undefined) {
			console.warn('Cannot load video - ' + video);
			return;
		}

		if (video.north_boundary === null || video.south_boundary === null || video.west_boundary === null || video.east_boundary === null) {
			console.error('Geolocated video did not contian location data!');
			return;
		}

		this._app.get('scene').addLoading('earth', 'video');

		const lowerLeft = new Pioneer.LatLonAlt(Pioneer.MathUtils.degToRad(parseFloat(video.south_boundary)), Pioneer.MathUtils.degToRad(parseFloat(video.west_boundary)), 0, true);
		const upperRight = new Pioneer.LatLonAlt(Pioneer.MathUtils.degToRad(parseFloat(video.north_boundary)), Pioneer.MathUtils.degToRad(parseFloat(video.east_boundary)), 0, true);

		const earth = this._scene.getEntity('earth');
		const earthSpheroidComponent = earth.getComponentByType('spheroid');
		const camera = this._scene.get('camera', 'camera');

		const patchComponent = earth.getComponent('patch');
		if (earth.getComponent('patchSpheroid') === null) {
			const patchSpheroid = earth.addComponent('spheroid', 'patchSpheroid');
			patchSpheroid.setEquatorialRadius(earth.get('spheroid', 0)._equatorialRadius + 3);
			patchSpheroid.setPolarRadius(earth.get('spheroid', 0)._polarRadius + 3);
			patchComponent.setSpheroidReference('patchSpheroid');
		}
		patchComponent.setVisible(false);
		patchComponent.setEnabled(true);

		if (!(earthSpheroidComponent instanceof Pioneer.SpheroidComponent) || !(patchComponent instanceof Pioneer.SpheroidLODComponent)) {
			return;
		}

		// Call getLoadedPromise first to make sure avoid a race condition when multiple textures are loading to an entitiy.
		await earthSpheroidComponent.getLoadedPromise();
		await patchComponent.getLoadedPromise();
		await this._pioneer.waitUntilNextFrame();

		// Set light global state to true.
		lightStore.setGlobalState({ fill: true });
		videoStore.setGlobalState({ isVideoPlaying: false, isVideoLoading: true });

		// Set patch component location and texture.
		patchComponent.setMapping('cylinder');
		patchComponent.setTexture('color', `${video.src}`);
		patchComponent.setBounds(lowerLeft, upperRight);
		patchComponent.setForceLoaded(true);

		// Wait for all entities to be loaded
		await this._pioneer.waitUntilNextFrame();
		await earthSpheroidComponent.getLoadedPromise();
		await patchComponent.getLoadedPromise();
		patchComponent.setVisible(true);

		// Stop the time rate.
		const { get } = refsStore.stateSnapshot;
		const timeManager = get('time');
		const previousTimeRate = timeManager.getTimeRate();
		get('time').setTimeRate(0);

		// Fly to video patch
		await get('camera').goToVideoPatch(videoId, video);

		// Reset the time rate.
		timeManager.setTimeRate(previousTimeRate);
		// setup the handler for when the video ends.
		this._videoTexture = patchComponent.getThreeJsMaterials()[0].uniforms.colorTexture.value.image;
		this._videoTexture.loop = true;
		this._app.get('scene').removeLoading('earth', 'video');
		this._videoTexture.play();
	}

	/**
	 * Toggles the play/pause state of a global or geoLocated video.
	 * Modal videos use the HTML video element controls.
	 */
	toggleVideoPlayPause() {
		const { isVideoPlaying } = videoStore.stateSnapshot;
		const { videoId } = videoStore.stateSnapshot;
		const videoType = this.getVideoType(videoId);

		// For global videos, use the earth SpheroidLOD component, for geoLocated videos, use the patch component.
		const componentName = videoType === 'global' ? 'basic' : 'patch';
		const earthComponent = this._scene.getEntity('earth').getComponent(componentName);
		const currentTime = earthComponent?.getThreeJsMaterials()[0]?.uniforms?.colorTexture?.value?.image?.currentTime;
		// Check to see if the video is loaded into the texture.
		if (currentTime !== 0 && !currentTime) {
			return;
		}
		if (isVideoPlaying) {
			earthComponent.getThreeJsMaterials()[0].uniforms.colorTexture.value.image.pause();
			videoStore.setGlobalState({ isVideoPlaying: false });
		} else {
			earthComponent.getThreeJsMaterials()[0].uniforms.colorTexture.value.image.play();
			videoStore.setGlobalState({ isVideoPlaying: true });
		}
	}
}

export default VideoManager;
